import React, { useState, useRef } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { UploadCloud, FileCheck2, ShieldCheck, Lock, Fingerprint, Loader2, CheckCircle2, Compass, ExternalLink } from "lucide-react";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { useAuth } from "@/context/AuthContext";
import api, { NODE_SPHERE_3D } from "@/lib/api";

export default function VerifyGenealogy({ embedded }) {
  const { user } = useAuth();
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [lineage, setLineage] = useState("");
  const [notes, setNotes] = useState("");
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const inputRef = useRef(null);

  const onDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files?.[0]) setFile(e.dataTransfer.files[0]);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!file || !title || !lineage) {
      toast.error("Please provide a title, lineage, and a document.");
      return;
    }
    setBusy(true);
    setResult(null);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title);
      fd.append("lineage", lineage);
      fd.append("notes", notes);
      const res = await api.post("/genealogy/submit", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setResult(res.data);
      toast.success("Record timestamped and anchored to the registry.");
      setFile(null);
      setTitle("");
      setLineage("");
      setNotes("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      {!embedded && (
        <PageHeader
          eyebrow="Verify Genealogy · Cryptographic & Archival Portal"
          title="Anchor your lineage to the Decentralized Blockchain Ancestry Registry."
          subtitle="Upload completed genealogical reports, historical censuses, and lineage charts for cryptographic verification and permanent, family-controlled preservation."
        />
      )}

      <section className="py-20 bg-ink">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 grid lg:grid-cols-12 gap-14">
          {/* Left: intake (only for authenticated members) */}
          {user && (
            <div className="lg:col-span-7">
              <Reveal>
                <form onSubmit={submit} data-testid="genealogy-form" className="border border-gold/20 bg-ink-surface p-8 sm:p-10">
                  <Eyebrow>Secure Intake Interface</Eyebrow>
                  <h3 className="font-serif text-3xl mt-4 mb-8">Submit a lineage record</h3>

                  <div className="space-y-7">
                    <div>
                      <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-gold/60">Record Title</label>
                      <input
                        data-testid="input-title"
                        value={title}
                        onChange={(e) => setTitle(e.target.value)}
                        placeholder="e.g. Maternal Lineage — Census 1900–1940"
                        className="w-full bg-transparent border-b border-gold/30 focus:border-gold outline-none py-3 mt-2 font-sans text-[15px] text-[#F5F0EB] placeholder:text-[#5f5c57] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-gold/60">Lineage / Heritage</label>
                      <input
                        data-testid="input-lineage"
                        value={lineage}
                        onChange={(e) => setLineage(e.target.value)}
                        placeholder="e.g. Woodland Mississippian · Modern Freedmen"
                        className="w-full bg-transparent border-b border-gold/30 focus:border-gold outline-none py-3 mt-2 font-sans text-[15px] text-[#F5F0EB] placeholder:text-[#5f5c57] transition-colors"
                      />
                    </div>
                    <div>
                      <label className="font-mono text-[10px] tracking-[0.24em] uppercase text-gold/60">Notes (optional)</label>
                      <textarea
                        data-testid="input-notes"
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                        rows={3}
                        placeholder="Context for verifiers…"
                        className="w-full bg-transparent border-b border-gold/30 focus:border-gold outline-none py-3 mt-2 font-sans text-[15px] text-[#F5F0EB] placeholder:text-[#5f5c57] transition-colors resize-none"
                      />
                    </div>

                    <div
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={onDrop}
                      onClick={() => inputRef.current?.click()}
                      data-testid="dropzone"
                      className="border border-dashed border-gold/30 hover:border-gold/60 cursor-pointer p-10 text-center transition-colors"
                    >
                      <input
                        ref={inputRef}
                        type="file"
                        data-testid="file-input"
                        accept=".pdf,.png,.jpg,.jpeg,.webp,.csv,.txt,.doc,.docx"
                        className="hidden"
                        onChange={(e) => setFile(e.target.files?.[0] || null)}
                      />
                      {file ? (
                        <div className="flex items-center justify-center gap-3 text-gold">
                          <FileCheck2 size={22} />
                          <span className="font-mono text-xs">{file.name}</span>
                        </div>
                      ) : (
                        <>
                          <UploadCloud size={30} className="text-gold/70 mx-auto" strokeWidth={1.3} />
                          <p className="font-sans text-sm text-[#C9C4BD] mt-4">Drop your document here, or click to browse</p>
                          <p className="font-mono text-[10px] tracking-[0.2em] uppercase text-[#5f5c57] mt-2">PDF · Images · CSV · Charts</p>
                        </>
                      )}
                    </div>

                    <button
                      type="submit"
                      disabled={busy}
                      data-testid="submit-genealogy"
                      className="w-full flex items-center justify-center gap-3 px-8 py-4 bg-gold text-black font-mono text-[11px] tracking-[0.22em] uppercase hover:bg-gold-text transition-colors disabled:opacity-60"
                    >
                      {busy ? (
                        <><Loader2 size={16} className="animate-spin" /> Encrypting & Timestamping…</>
                      ) : (
                        <><Fingerprint size={15} /> Anchor to Registry</>
                      )}
                    </button>
                  </div>

                  {result && (
                    <div data-testid="submit-result" className="mt-8 border border-gold/30 bg-ink p-6">
                      <div className="flex items-center gap-2 text-gold">
                        <CheckCircle2 size={18} />
                        <span className="font-mono text-[11px] tracking-[0.2em] uppercase">{result.status}</span>
                      </div>
                      <dl className="mt-5 space-y-3 font-mono text-[11px]">
                        <div className="flex justify-between gap-4"><dt className="text-[#7a766f]">BLOCK</dt><dd className="text-gold-text">#{result.block_number}</dd></div>
                        <div className="flex justify-between gap-4"><dt className="text-[#7a766f]">FILE HASH</dt><dd className="text-gold-text truncate max-w-[220px]">{result.file_hash}</dd></div>
                        <div className="flex justify-between gap-4"><dt className="text-[#7a766f]">BLOCK HASH</dt><dd className="text-gold-text truncate max-w-[220px]">{result.block_hash}</dd></div>
                      </dl>
                    </div>
                  )}
                </form>
              </Reveal>
            </div>
          )}

          {/* Right: notices */}
          <div className={user ? "lg:col-span-5 space-y-8" : "lg:col-span-12 max-w-2xl mx-auto space-y-8"}>
            <Reveal>
              <div className="border border-gold/20 overflow-hidden bg-ink relative">
                <motion.img
                  src={NODE_SPHERE_3D}
                  alt="Decentralized registry visualization"
                  className="w-full h-[220px] object-cover"
                  animate={{ scale: [1, 1.07, 1] }}
                  transition={{ duration: 16, repeat: Infinity, ease: "easeInOut" }}
                />
                <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-ink to-transparent p-5">
                  <p className="font-mono text-[10px] tracking-[0.22em] uppercase text-gold/70">Live Registry · Decentralized Nodes</p>
                </div>
              </div>
            </Reveal>
            <Reveal delay={0.1}>
              <div className="border border-gold/15 bg-ink-surface p-8">
                <Fingerprint size={24} className="text-gold" strokeWidth={1.3} />
                <h4 className="font-serif text-2xl mt-5">Decentralized Technology</h4>
                <p className="font-sans text-sm text-[#A19D98] leading-relaxed mt-4">
                  The Trust utilizes the most modern decentralized blockchain architecture to securely
                  timestamp, encrypt, and store genealogical records permanently. Each submission is
                  hashed with SHA-256 and anchored as an immutable block.
                </p>
              </div>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="border border-gold/15 bg-ink-surface p-8">
                <ShieldCheck size={24} className="text-gold" strokeWidth={1.3} />
                <h4 className="font-serif text-2xl mt-5">Data Sovereignty Guarantee</h4>
                <p className="font-sans text-sm text-[#A19D98] leading-relaxed mt-4">
                  Your family trees and ancestral archives remain private, unalterable, and entirely
                  controlled by the family lineage — never by external government bodies. You hold the keys
                  to your heritage.
                </p>
              </div>
            </Reveal>
            {!user && (
              <Reveal delay={0.25}>
                <div className="border border-gold/25 bg-ink-surface p-8 text-center" data-testid="verify-vault-gate">
                  <Lock size={28} className="text-gold mx-auto" strokeWidth={1.3} />
                  <h4 className="font-serif text-2xl mt-5">Secure Intake Requires the Vault</h4>
                  <p className="font-sans text-sm text-[#A19D98] leading-relaxed mt-4">
                    To protect data sovereignty, genealogical submissions are accepted only through an
                    authenticated Vault session. Sign in to open the secure intake interface.
                  </p>
                  <Link to="/login" data-testid="verify-login-cta" className="inline-flex items-center gap-2 mt-7 px-8 py-4 bg-gold text-black font-mono text-[11px] tracking-[0.2em] uppercase hover:bg-gold-text transition-colors">
                    <Lock size={14} /> Enter the Vault
                  </Link>
                </div>
              </Reveal>
            )}
            <Reveal delay={0.3}>
              <div data-testid="verify-partner" className="border border-gold/25 bg-ink-surface p-8">
                <Compass size={24} className="text-gold" strokeWidth={1.3} />
                <h4 className="font-serif text-2xl mt-5">Haven't Traced Your Lineage Yet?</h4>
                <p className="font-sans text-sm text-[#A19D98] leading-relaxed mt-4">
                  Before anchoring a record, you need completed genealogical research. Our trusted partner
                  <span className="text-gold-text"> Find Ya Tribe</span> helps descendants search their
                  lineages and trace their heritage to gather the documentation required here.
                </p>
                <a
                  href="https://findyatribe.org"
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="verify-partner-link"
                  className="group inline-flex items-center gap-3 mt-6 font-mono text-[11px] tracking-[0.2em] uppercase text-gold border-b border-gold/40 pb-1 hover:border-gold transition-colors"
                >
                  Visit findyatribe.org <ExternalLink size={14} className="group-hover:translate-x-1 transition-transform" />
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
}
